import classnames from 'classnames'
import { useEffect, useState } from 'react'

import { getGuestId, hasToken } from '../../lib/identity'
import { getPlayerName, setPlayerName } from '../../lib/leaderboard'
import { getStoredIsHighContrastMode } from '../../lib/localStorage'
import { beginGame } from '../../lib/progress'
import { CharStatus } from '../../lib/statuses'
import { Cell } from '../grid/Cell'

const SEEN_KEY = 'welcomeSeen'

// The intro plays one tiny round: WOWZA flips in with the board's own tile
// colors, so the overlay reads as part of the game before the guest starts.
const INTRO_WORD: { letter: string; status: CharStatus }[] = [
  { letter: 'W', status: 'correct' },
  { letter: 'O', status: 'present' },
  { letter: 'W', status: 'absent' },
  { letter: 'Z', status: 'correct' },
  { letter: 'A', status: 'correct' },
]

// Scope the "seen the welcome overlay" flag per identity, so opening a
// different guest's link in the same browser re-shows the welcome overlay for
// that guest instead of silently skipping it. Anonymous/dev play (no token)
// keeps the legacy un-scoped key.
// Called when the server shows this guest already started today's puzzle
// (e.g. on a new device), so the intro doesn't reappear mid-game.
export const markWelcomeSeen = (): void => {
  localStorage.setItem(seenKey(), '1')
}

const seenKey = (): string => {
  const id = getGuestId()
  return id ? `${SEEN_KEY}:${id}` : SEEN_KEY
}

type Props = {
  // Opens the How-To-Play (instructions) modal.
  onShowInstructions: () => void
  // Puzzle identifier; used to stamp started_at server-side on Start.
  puzzleDay: number
}

// Full-screen intro shown the first time the page is opened. Reveals the
// surprise, points to the instructions, and gates entry behind a Start button.
// When a signed guest token is present, the player's name comes from the token
// (no free-text field); otherwise a Full Name is collected for anonymous play.
export const WelcomeOverlay = ({ onShowInstructions, puzzleDay }: Props) => {
  const signedIn = hasToken()
  const [visible, setVisible] = useState(false)
  const [name, setName] = useState(getPlayerName())

  useEffect(() => {
    if (!localStorage.getItem(seenKey())) {
      setVisible(true)
    }
  }, [])

  // While the overlay is open, flag the body so the game's global key
  // listener ignores keystrokes (prevents Enter/letters leaking into the grid).
  // Clearing is handled by start() (deferred past the dismissing keyup) and by
  // the final unmount cleanup — NOT here on visible=false, which would race the
  // trailing keyup and clear the flag too early.
  useEffect(() => {
    if (visible) {
      document.body.dataset.overlayOpen = '1'
    }
  }, [visible])

  // Backstop: ensure the flag is cleared if the component unmounts.
  useEffect(() => {
    return () => {
      delete document.body.dataset.overlayOpen
    }
  }, [])

  const trimmedName = name.replace(/\s+/g, ' ').trim()
  // Signed-in guests already have an identity from the token; anonymous play
  // still requires a typed name.
  const canStart = signedIn || trimmedName.length >= 1

  const start = () => {
    if (!canStart) return
    if (!signedIn) setPlayerName(trimmedName)
    localStorage.setItem(seenKey(), '1')
    // Stamp started_at server-side (idempotent) now that they are committing to
    // play. Fire-and-forget; local play is unaffected if it fails.
    void beginGame(puzzleDay)
    // The dismissing key press (Enter on the Start button) produces a trailing
    // keyup that lands on <body> AFTER the overlay unmounts, which would leak
    // into the grid as a submit ("Not enough letters"). Instead of a flag
    // cleared on the next frame (fragile vs. keyup/rAF ordering), stamp the
    // dismiss time; the grid key listener ignores keys for a short window after
    // this. Keep the open-flag set through this frame as a belt-and-suspenders.
    document.body.dataset.overlayClosedAt = String(Date.now())
    requestAnimationFrame(() => {
      delete document.body.dataset.overlayOpen
    })
    setVisible(false)
  }

  if (!visible) return null

  // Match the board's palette, including high-contrast mode.
  const isHighContrast = getStoredIsHighContrastMode()

  return (
    <div
      className="fixed inset-0 z-[5] flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-white px-6 pt-7 pb-6 text-center shadow-xl dark:bg-slate-800 sm:px-8">
        <div className="mb-6 flex justify-center" aria-hidden="true">
          {INTRO_WORD.map(({ letter, status }, i) => (
            <Cell
              key={i}
              value={letter}
              status={status}
              isRevealing
              isCompleted
              position={i}
            />
          ))}
        </div>

        <h1
          id="welcome-title"
          className="text-3xl font-extrabold leading-tight tracking-tight text-gray-900 [text-wrap:balance] dark:text-white"
        >
          We added a little surprise!
        </h1>

        <p className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
          Play this Wordle to unlock the Save the Date.
        </p>
        <p className="mt-1 text-base leading-relaxed text-gray-600 dark:text-gray-300">
          Guess the 5-letter word in 6 tries. After each guess, the tiles change
          color to show how close you are.{' '}
          <button
            type="button"
            onClick={onShowInstructions}
            className={classnames(
              'font-semibold text-gray-900 underline decoration-2 underline-offset-4 dark:text-white',
              isHighContrast
                ? 'decoration-orange-500 hover:text-orange-700 dark:hover:text-orange-300'
                : 'decoration-green-500 hover:text-green-700 dark:hover:text-green-300'
            )}
          >
            How to play
          </button>
        </p>

        {!signedIn && (
          <div className="mt-5 text-left">
            <label
              htmlFor="welcome-name"
              className="block text-sm font-medium text-gray-900 dark:text-gray-100"
            >
              Full Name
            </label>
            <input
              id="welcome-name"
              type="text"
              maxLength={40}
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && start()}
              placeholder="Your name"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-base text-gray-900 focus:border-green-600 focus:outline-none focus:ring-1 focus:ring-green-600 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
            />
          </div>
        )}

        <button
          type="button"
          onClick={start}
          disabled={!canStart}
          className={classnames(
            'mt-7 w-full rounded-md px-4 py-3 text-base font-bold uppercase tracking-wide text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-offset-slate-800',
            isHighContrast
              ? 'bg-orange-700 hover:bg-orange-800 focus-visible:ring-orange-500'
              : 'bg-green-700 hover:bg-green-800 focus-visible:ring-green-500'
          )}
        >
          Start the challenge
        </button>
        {signedIn && (
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
            Playing as{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {getPlayerName()}
            </span>
          </p>
        )}
      </div>
    </div>
  )
}
